import { useEffect, useState } from "react";
import { IDropdown, ILeadReq, TripType } from "../../../models/BaseModel";
import { Button } from "primereact/button";
import { Badge } from "primereact/badge";
import { Fetch } from "../../../shared/Fetch";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Calendar } from "primereact/calendar";
import { MultiSelect } from "primereact/multiselect";

interface IVehicleTypeDrop extends IDropdown {
    dayCharge: number
    driverCharge: number
    nightCharge: number
    minCharge: number
    capacity: number
    ratePerKm: number
}
interface IPickupLocationDrop extends IDropdown {
    distance: number
    noOfTols: number
    parkingCharge: number
    tollCharge: number
}
interface IPickAndDrops {
    id?: number
    pickupLocationId?: number
    noOfPeople?: number
    tripType?: TripType
    vehicles?: {
        vehicleTypeId: number, count: number,
        ratePerKm: number,
        minCharge: number,
        driverCharge: number,
        dayCharge: number,
        nightCharge: number,
        parkingCharge: number,
        tollCharge: number
    }[]
    time?: Date
    endtime?: Date
    removeIt?: boolean
    tempId: number
}

interface IProps {
    req: ILeadReq
    open: boolean
    onSuccess: () => void
    onHide: () => void
    toast: any
}

export const Transport = ({ req, toast, onSuccess, open, onHide }: IProps) => {
    const tripTypes = ['pickup', 'drop', 'roundtrip'];
    const [vehicleTypes, setVehicleTypes] = useState<IVehicleTypeDrop[]>();
    const [pickupLocation, setPickupLocation] = useState<IPickupLocationDrop[]>();
    const [pickAndDrops, setPickAndDrops] = useState<IPickAndDrops[]>();
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (open) {
            if (req.pickAndDrops && req.pickAndDrops.length > 0) {
                const _pickAndDrops = [...req.pickAndDrops]
                    .map(pd => ({ ...pd, time: pd.time ? new Date(pd.time) : undefined }))
                    .sort((a, b) => (a.time?.getTime() ?? 0) - (b.time?.getTime() ?? 0))
                    .map((pd, i) => ({ ...pd, tempId: i + 1 }));
                setPickAndDrops(_pickAndDrops);
            } else if (req.id) {
                setPickAndDrops([{ tempId: 1, vehicles: [], noOfPeople: (req.noOfAdults ?? 0) + (req.noOfChildren ?? 0), tripType: 'pickup' }]);
            }
        }
    }, [req.rooms]);

    function isTripType(val: any): val is TripType {
        return tripTypes.includes(val);
    }

    const loadVehicleTypes = async (cityId: number) => {
        const response = await Fetch(`vehicletype/dropdown/${cityId}`, "GET", null, null, null);
        if (response) {
            setVehicleTypes(await response.json());
        }
    }
    const loadPickupLocation = async (hotelId: number) => {
        const response = await Fetch(`pickuplocation/dropdown/${hotelId}`, "GET", null, null, null);
        if (response) {
            setPickupLocation(await response.json());
        }
    }
    useEffect(() => {
        if (open && req.hotelId) loadPickupLocation(req.hotelId);
    }, [req.hotelId]);
    useEffect(() => { if (open && req.cityId) loadVehicleTypes(req.cityId) }, [req.cityId]);

    function peopleleftvehicle(pd: IPickAndDrops): number {
        if (pd.noOfPeople && pd.vehicles && pd.vehicles.length > 0) {
            const totalCapacity = pd.vehicles.reduce((acc, vt) => {
                const capacity = vehicleTypes?.find(v => v.id === vt.vehicleTypeId)?.capacity || 0;
                return acc + (capacity * vt.count);
            }, 0);
            return pd.noOfPeople - totalCapacity;
        }
        return pd.noOfPeople || 0;
    }

    async function save() {
        setLoading(true);
        if (!pickAndDrops || pickAndDrops.length === 0) {
            toast.current.show({ severity: 'error', detail: `Please add transport`, life: 3000 });
            setLoading(false);
            return;
        } else if (pickAndDrops.length > 0) {
            let error = false;
            pickAndDrops.forEach(e => {
                if (!error && !e.removeIt && (e.noOfPeople === undefined || e.pickupLocationId === undefined || e.pickupLocationId < 1 || !e.tripType || peopleleftvehicle(e) > 0)) error = true;
            });
            if (error) {
                toast.current.show({ severity: 'error', detail: `Please fill no of people, location, triptype and vehicles for pick&drop`, life: 3000 });
                setLoading(false);
                return;
            }
        }
        const body = pickAndDrops?.map(pd => ({ id: pd.id, pickupLocationId: pd.pickupLocationId ?? 0, noOfPeople: pd.noOfPeople ?? 0, tripType: pd.tripType ?? 'roundtrip', vehicles: pd.vehicles, removeIt: pd.removeIt, time: pd.time, endtime: pd.endtime }));
        const response = await Fetch(`leadreq/transport/${req.id}`, 'POST', body, null, toast);
        if (response) {
            toast.current.show({ severity: 'success', detail: `Transport updated`, life: 3000 });
            onSuccess();
        }
        setLoading(false);
    }

    function addCountVehicle(pickAndDrop: IPickAndDrops, vehicleTypeId: number) {
        setPickAndDrops(prev => {
            if (!prev) return prev;

            return prev.map(pd => {
                if (pd.tempId !== pickAndDrop.tempId) return pd;

                const vehicles = pd.vehicles ? [...pd.vehicles] : [];
                const index = vehicles.findIndex(v => v.vehicleTypeId === vehicleTypeId);

                if (index >= 0) {
                    vehicles[index] = {
                        ...vehicles[index],
                        count: vehicles[index].count + 1
                    };
                } else {
                    const vt = vehicleTypes?.find(vt => vt.id === vehicleTypeId);
                    const location = pickupLocation?.find(p => p.id === pd.pickupLocationId);
                    vehicles.push({ vehicleTypeId, count: 1, ratePerKm: vt?.ratePerKm || 0, minCharge: vt?.minCharge || 0, driverCharge: vt?.driverCharge || 0, dayCharge: vt?.dayCharge || 0, nightCharge: vt?.nightCharge || 0, parkingCharge: location?.parkingCharge || 0, tollCharge: location?.tollCharge || 0 });
                }

                return { ...pd, vehicles };
            });
        });
    }

    function removeCountVehicle(pickAndDrop: IPickAndDrops, vehicleTypeId: number) {
        setPickAndDrops(prev => {
            if (!prev) return prev;

            return prev.map(pd => {
                if (pd.tempId !== pickAndDrop.tempId) return pd;

                const vehicles = pd.vehicles ? [...pd.vehicles] : [];
                const index = vehicles.findIndex(v => v.vehicleTypeId === vehicleTypeId);

                if (index >= 0) {
                    const updatedCount = vehicles[index].count - 1;

                    if (updatedCount > 0) {
                        vehicles[index] = {
                            ...vehicles[index],
                            count: updatedCount
                        };
                    } else {
                        vehicles.splice(index, 1);
                    }
                }

                return { ...pd, vehicles };
            });
        });
    }

    function onPickAndDropsValueChange(pickAndDrop: IPickAndDrops, isLocation: boolean, val: string, isTime = 0, time?: any) {
        if (isTime !== 0) {
            const updatedPickAndDrops = pickAndDrops?.map(pd => {
                if (pd.tempId === pickAndDrop.tempId) {
                    if (isTime === 1) return { ...pd, time: time }
                    else return { ...pd, endtime: time }
                } else return { ...pd }
            });
            setPickAndDrops(updatedPickAndDrops);
        } else {
            const _val = +val;
            const value = val === '0' ? 0 : (_val > 0 ? _val : undefined);
            const updatedPickAndDrops = pickAndDrops?.map(pd => {
                if (pd.tempId === pickAndDrop.tempId) {
                    if (isTripType(val)) return { ...pd, tripType: val }
                    else if (isLocation) return { ...pd, pickupLocationId: value }
                    else {
                        return { ...pd, noOfPeople: value }
                        // const maxNoOfPeople = +noOfAdults + +noOfChildren;
                        // if (value && value > maxNoOfPeople) {
                        //     props.toast.current.show({ severity: 'error', detail: `No of passengers can't be more than ${maxNoOfPeople}`, life: 3000 });
                        //     return { ...pd, noOfPeople: maxNoOfPeople }
                        // } else return { ...pd, noOfPeople: value }
                    }
                } else return { ...pd }
            });
            setPickAndDrops(updatedPickAndDrops);
        }
    }
    function removeFromPickAndDrops(travel: IPickAndDrops, remeoveItFalse = false) {
        if (remeoveItFalse) {
            const updatedPickAndDrops = pickAndDrops?.map((pd) => {
                if (pd.tempId === travel.tempId) {
                    return { ...pd, removeIt: false };
                } else return { ...pd }
            });
            setPickAndDrops(updatedPickAndDrops);
        } else {
            const updatedPickAndDrops = pickAndDrops?.reduce<IPickAndDrops[]>((acc, pd) => {
                if (pd.tempId === travel.tempId) {
                    if (pd.id) {
                        acc.push({ ...pd, removeIt: true });
                    }
                } else {
                    acc.push({ ...pd });
                }
                return acc;
            }, []);
            setPickAndDrops(updatedPickAndDrops);
        }
    }

    const onVehiclesChange = (pickAndDrop: IPickAndDrops, vehicles: any) => {
        setPickAndDrops(prev => {
            if (!prev) return prev;

            return prev.map(pd => {
                if (pd.tempId !== pickAndDrop.tempId) return pd;

                const updatedVehicles = vehicles.map((v: any) => {
                    const existing = pd.vehicles?.find(p => p.vehicleTypeId === v.id);
                    const count = existing?.count && existing.count > 0 ? existing.count : 1;
                    const vt = vehicleTypes?.find(vt => vt.id === v.id);
                    const location = pickupLocation?.find(p => p.id === pd.pickupLocationId);
                    return { vehicleTypeId: v.id, count, ratePerKm: vt?.ratePerKm || 0, minCharge: vt?.minCharge || 0, driverCharge: vt?.driverCharge || 0, dayCharge: vt?.dayCharge || 0, nightCharge: vt?.nightCharge || 0, parkingCharge: location?.parkingCharge || 0, tollCharge: location?.tollCharge || 0 };
                });

                return { ...pd, vehicles: updatedVehicles };
            });
        });
    }


    return (
        <Dialog visible={open} style={{ width: '80vw' }} header={`Add/Edit Transport (${req.hotelName}, ${req.cityName}, ${req.stateName})`} modal className="p-fluid" onHide={onHide}>
            <div className="p-fluid formgrid grid">
                {pickAndDrops?.map((pd) =>
                    <div key={pd.tempId} className="field col-12 md:col-12">
                        <div className="card flex-1 p-2" style={{
                            position: 'relative', backgroundImage: pd.id && pd.removeIt ? 'linear-gradient(to bottom,rgb(255, 200, 200), rgb(255, 150, 150))' :
                                !pd.id ? 'linear-gradient(to bottom, rgb(200, 255, 200), rgb(150, 255, 150))' : 'linear-gradient(to bottom, #ffffff, #f0f0f0)'
                        }}>
                            {pd.removeIt ? <Button style={{ height: '20px', width: '20px', margin: '5px', position: 'absolute', top: -3, right: -3 }} onClick={() => removeFromPickAndDrops(pd, true)} icon="pi pi-times" type="button" className="p-button-secondary" />
                                : <Button style={{ height: '20px', width: '20px', margin: '5px', position: 'absolute', top: -3, right: -3 }} onClick={() => removeFromPickAndDrops(pd)} icon="pi pi-trash" type="button" className="p-button-danger" />}
                            <div className="p-fluid formgrid grid">
                                <div className="field col-12 md:col-3">
                                    <label>Passengers</label><InputText value={pd.noOfPeople ?? ''} type="number" onChange={(e) => onPickAndDropsValueChange(pd, false, e.target.value)} />
                                </div>
                                <div className="field col-12 md:col-3">
                                    <label>Trip Type</label><Dropdown options={tripTypes} value={pd.tripType} onChange={(e) => { onPickAndDropsValueChange(pd, true, e.target.value) }} />
                                </div>
                                <div className="field col-12 md:col-3">
                                    <label>{pd.tripType === 'pickup' ? 'Pickup Location' : pd.tripType === 'drop' ? 'Drop Location' : 'Pickup Location'}</label>
                                    <Dropdown options={pickupLocation} value={pd.pickupLocationId} optionLabel="name" optionValue="id" onChange={(e) => { onPickAndDropsValueChange(pd, true, e.target.value) }} />
                                </div>
                                <div className="field col-12 md:col-3">
                                    <label>{pd.tripType === 'pickup' ? 'Pickup Time' : pd.tripType === 'drop' ? 'Drop Time' : 'Pickup Time'}</label>
                                    <Calendar minDate={req.checkin ? new Date(new Date(req.checkin).setHours(0, 0, 0, 0)) : new Date()} maxDate={new Date(req.checkout || new Date())} value={pd.time ? new Date(pd.time) : undefined} showTime dateFormat="dd-mm-yy" hourFormat="12" onChange={(e) => { onPickAndDropsValueChange(pd, false, '', 1, e.value) }} />
                                </div>
                                {pd.tripType === 'roundtrip' && pd.time && <div className="field col-12 md:col-3">
                                    <label>Drop At</label>
                                    <Calendar minDate={new Date(pd.time)} maxDate={new Date(req.checkout || new Date())} value={pd.endtime ? new Date(pd.endtime) : undefined} showTime dateFormat="dd-mm-yy" hourFormat="12" onChange={(e) => { onPickAndDropsValueChange(pd, false, '', 2, e.value) }} />
                                </div>}
                                <div className="field col-12 md:col-12">
                                    <label>Vehicles</label>
                                    <MultiSelect options={vehicleTypes} optionLabel="name" placeholder="Select Vehicle Types" required value={vehicleTypes?.filter(v => pd.vehicles?.map(e => e.vehicleTypeId).includes(v.id))} onChange={e => onVehiclesChange(pd, e.target.value)} />
                                </div>
                                {peopleleftvehicle(pd) > 0 && <div className="field col-12 md:col-12"><span className="p-error">Please select vehicle types for {peopleleftvehicle(pd)} more people</span></div>}
                                {pd.vehicles?.map((v) =>
                                    <div key={v.vehicleTypeId} className="field col-12 md:col-3">
                                        <div className="card flex-1 p-0" style={{ width: '250px', position: 'relative', backgroundImage: 'linear-gradient(to bottom, #ffffff, #f0f0f0)' }}>
                                            <div style={{ padding: '10px' }}>
                                                <label>{vehicleTypes?.find(e => e.id === v.vehicleTypeId)?.name}</label>
                                                <div><small className="p-info" >{`${vehicleTypes?.find(e => e.id === v.vehicleTypeId)?.capacity} people. ${vehicleTypes?.find(e => e.id === v.vehicleTypeId)?.ratePerKm ?? '--'}rs/km`}</small></div>
                                            </div>
                                            <Badge style={{ position: 'absolute', top: -5, right: -5 }} value={v.count} severity="warning"></Badge>
                                            <div className="flex" style={{ position: 'absolute', bottom: 0, right: 0 }}>
                                                <Button style={{ height: '20px', width: '20px', margin: '5px' }} onClick={() => removeCountVehicle(pd, v.vehicleTypeId)} icon="pi pi-minus" type="button" className="p-button-danger" />
                                                <Button style={{ height: '20px', width: '20px', margin: '5px' }} onClick={() => addCountVehicle(pd, v.vehicleTypeId)} icon="pi pi-plus" type="button" className="p-button-success" />
                                            </div>
                                        </div>
                                    </div>)}
                            </div>
                        </div>
                    </div>)}
                <div className="field col-12 md:col-12 flex">
                    <Button label="Add More" onClick={() => {
                        if (pickAndDrops && pickAndDrops.length) setPickAndDrops([...pickAndDrops, { tempId: pickAndDrops.length + 1, vehicles: [] }])
                        else setPickAndDrops([{ tempId: 1, vehicles: [], noOfPeople: (req.noOfAdults ?? 0) + (req.noOfChildren ?? 0), tripType: 'pickup' }])
                    }} icon="pi pi-plus" type="button" style={{ width: '200px' }} className="p-button-sm p-button-success"></Button>
                </div>
                <div className="col-12 md:col-12 flex gap-4 justify-content-end">
                    <Button style={{ width: '100px' }} loading={loading} label="SAVE" onClick={save} type="button" className="p-button-rounded"></Button>
                    <Button style={{ width: '100px' }} label="CLOSE" onClick={onHide} type="button" className="p-button-rounded p-button-outlined surface-200"></Button>
                </div>
            </div>
        </Dialog>
    );
}